import * as React from 'react'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import CloseIcon from '@mui/icons-material/Close'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks'
import { convertToAbsoluteTimeRange } from 'components/DatePickerPopover/dateRangeUtils'
import { RelativeTimeRangeLabel, TimeRange } from 'components/DatePickerPopover/types'
import DatePickerPopover from 'components/DatePickerPopover/DatePickerPopover'
import colors from 'lib/colors'
import moment from 'moment-timezone'

export type LeadTableDateFilterValue = {
  operator: 'between'
  value: {
    label: RelativeTimeRangeLabel | null
    timeRange: [string, string]
  }
}

type LeadTableDateFilterProps = {
  title: string
  timezone: string
  value?: LeadTableDateFilterValue
  isActiveOverride?: boolean
  onChange: (value: LeadTableDateFilterValue | undefined) => void
  onClear?: () => void
}

function hasDateFilter(value?: LeadTableDateFilterValue) {
  return (
    value?.operator === 'between' &&
    !!(value.value.label || value.value.timeRange)
  )
}

export default function LeadTableDateFilter({
  title,
  timezone,
  value,
  isActiveOverride,
  onChange,
  onClear,
}: LeadTableDateFilterProps) {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: `lead-table-date-filter-${title}`,
  })

  const isActive = isActiveOverride ?? hasDateFilter(value)

  const handleClear = React.useCallback(
    (event: React.MouseEvent<SVGSVGElement>) => {
      event.stopPropagation()

      if (onClear) {
        onClear()
        return
      }

      onChange(undefined)
    },
    [onChange, onClear]
  )

  const handleChange = React.useCallback(
    (timeRange: TimeRange) => {
      const absoluteTimeRange = convertToAbsoluteTimeRange(timeRange, timezone)

      if (!absoluteTimeRange) {
        onChange(undefined)
        return
      }

      onChange({
        operator: 'between',
        value: {
          label: timeRange.label ?? null,
          timeRange: [
            absoluteTimeRange[0].format(),
            absoluteTimeRange[1].format(),
          ],
        },
      })
    },
    [onChange, timezone]
  )

  const initialValue = React.useMemo((): TimeRange => {
    if (value?.value.label) {
      return { label: value.value.label }
    }

    if (value?.value.timeRange) {
      return {
        label: undefined,
        timeRange: [
          moment(value.value.timeRange[0]).tz(timezone),
          moment(value.value.timeRange[1]).tz(timezone),
        ],
      }
    }

    return { label: 'Lifetime' }
  }, [value, timezone])

  return (
    <>
      <Button
        variant='outlined'
        size='small'
        {...bindTrigger(popupState)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 'unset',
          position: 'relative',
          padding: '6px !important',
          borderColor: isActive ? colors.frenchBlue : colors.lightSlateGray,
          '& .icon-layer': {
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          '& .clear-icon': {
            display: 'none',
          },
          '&:hover .filter-icon': {
            display: isActive ? 'none' : undefined,
          },
          '&:hover .clear-icon': {
            display: isActive ? 'flex' : 'none',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 18,
            height: 18,
            flexShrink: 0,
          }}
        >
          <Box className='icon-layer filter-icon'>
            <CalendarMonthIcon
              sx={{
                fontSize: 18,
                color: isActive ? colors.frenchBlue : colors.slateGray,
              }}
            />
          </Box>

          <Box className='icon-layer clear-icon'>
            <Tooltip title='Clear Filter'>
              <CloseIcon
                onClick={handleClear}
                aria-label={`Clear Filter ${title}`}
                sx={{ fontSize: 18 }}
              />
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            height: '18px',
            pr: 0.5,
            fontSize: '12px',
            lineHeight: '18px',
            color: isActive ? colors.frenchBlue : colors.slateGray,
          }}
        >
          {title}
        </Box>
      </Button>

      <DatePickerPopover
        {...bindPopover(popupState)}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onChange={handleChange}
        initialValue={initialValue}
        timeInputsEnabled
      />
    </>
  )
}
